import { SelectChangeEvent, Stack } from "@mui/material"
import {
    Button,
    Checkbox,
    Chip,
    ColorTextField,
    copyToClipboard,
    DialogHandler,
    Dropdown,
    EEditorType,
    EnhancedFormModel,
    EPopupBranding,
    EPopupCloseButtonPosition,
    EPopupDisplayFrequency,
    EPopupPosition,
    EPopupTriggerType,
    fetchRoute,
    FormsFactory,
    FullBar,
    GenericWrapperContext,
    getBrandUrl,
    getStorage,
    Icon,
    InlineTextEdit,
    ListModel,
    ListsFactory,
    LoadingContainer,
    Modal,
    PopupModel,
    PopupsFactory,
    ResourceEdit,
    setStorage,
    TextField,
    TPopupModel,
    TPopupTrigger,
    Typography
} from "@cakemail-org/ui-components-v2"
import { Trans, useTranslation } from "react-i18next"
import { useContext, useState, useEffect, useMemo, ChangeEventHandler, ChangeEvent, useRef } from "react"
import { ERoutes } from "../../../utils/url"
import { TButtonRef } from "@cakemail-org/ui-components-v2/dist/esm/components/button/types"
import { useLocation, useParams } from "react-router-dom"
import BeeEditor from "../../../components/beeEditor"
import { ResourcePreview } from "../../../components/Resources/preview"
import "../styles.scss"
import { ResourceCopy } from "../../../components/Resources/copy"
import { AddTagsButton } from "../../../components/Tags"
import { TEditPopup } from "../types"
import { logError, setDataInPath } from "../../../utils/data"
import { ELogErrorMsg } from "../../../utils/data/types"
import { PopupSampleQueryCondition } from "../../../components/filtering/queryComponents/condition/popupSample"
import { FromCommonResourceEdit } from "../../forms/components/commonResource"
import { buildDefaultUrls, updateFormLayoutOptions } from "../../forms/utils"
import ResourceDetailContainer from "../../../components/contentTemplate/resourceDetailContainer"


export default function PopupDetails({ }: TEditPopup) {
    const genericWrapper = useContext(GenericWrapperContext)
    const { t } = useTranslation()
    const { popupId } = useParams()

    const location = useLocation()

    const [isEditing, setIsEditing] = useState<boolean>(location.pathname.split('/').pop() === 'edit')

    const [popup, setPopup] = useState<PopupModel | undefined>(undefined)
    const [form, setForm] = useState<EnhancedFormModel | undefined>(undefined)

    const [selectedList, setSelectedList] = useState<ListModel>()
    const [previewContent, setPreviewContent] = useState<string | undefined>(undefined)

    const [loading, setLoading] = useState<boolean>(false)
    const [renderLoading, setRenderLoading] = useState<boolean>(false)

    useEffect(() => {
        fetchPopup().then((response) => {
            if (response?.list_id) {
                ListsFactory.get({ id: response.list_id })
                    .then((list) => {
                        setSelectedList(list)
                    })
            }
        })
    }, [popupId])

    async function fetchPopup() {
        setLoading(true)
        return PopupsFactory.get({ id: popupId || "" })
            .then(async (popup) => {
                if (popup?.form_id) {
                    await fetchForm(popup.form_id).then(async (form) => {
                        popup.content.json = await updateFormLayoutOptions({
                            content: popup.content.json,
                            listId: popup.list_id,
                            submissionUrl: form.submission_url,
                            recaptchaSiteKey: form.recaptcha.site_key
                        })
                    })
                }

                setPopup(popup)
                renderPopup(popup)
                return popup
            }).catch(() => {
                DialogHandler.show({
                    type: "warning",
                    title: t("general-error-msg")
                })
                window.location.href = "#" + fetchRoute({ route: ERoutes.popups })
            })
            .finally(() => {
                setLoading(false)
            })
    }

    async function fetchForm(id: string): Promise<EnhancedFormModel> {
        return FormsFactory.get({ id: id })
            .then((form) => {
                setForm(form as EnhancedFormModel)
                return form as EnhancedFormModel
            })
    }

    async function renderPopup(cPopup?: PopupModel) {
        setRenderLoading(true)
        const popupToRender = cPopup || popup
        if (popupToRender) {
            popupToRender.render().then((content) => {
                setPreviewContent(content)
            }).catch(() => {
                setPreviewContent("")
            }).finally(() => {
                setRenderLoading(false)
            })
        }
    }

    function handlePublish(e: React.MouseEvent<HTMLButtonElement>, btn: TButtonRef) {
        if (popup) {
            validateFormUrls()
            popup.publish().then((popup) => {
                const showPublishedDialog = getStorage({ key: "popup-published-copy" })
                if (!showPublishedDialog || showPublishedDialog === false) {
                    DialogHandler.show({
                        title: t("popups-details-published-dialog-title"),
                        content: <Stack gap={4}>
                            <Typography variant="body1">{t("popups-details-published-dialog-desc")}</Typography>
                            <Stack gap={4} direction="row" alignItems="center">
                                <Checkbox name="popup-published-copy" value={true} onChange={(e) => {
                                    setStorage({ key: "popup-published-copy", data: e.target.checked ? true : false })
                                }} />
                                <Typography>{t("general-dont-show-again")}</Typography>
                            </Stack>
                        </Stack>,
                        primaryBtnOnClick: () => {
                            copyToClipboard(popup?.content.published_content || "")
                            DialogHandler.hide()
                        },
                        primaryBtnText: t("general-copy-close"),
                        secondaryBtnText: t("general-close")
                    })
                } else {
                    DialogHandler.show({
                        type: "success",
                        title: t("general-published")
                    })
                }
                setPopup(popup)
            }).catch((error) => {
                DialogHandler.show({
                    type: "error",
                    title: t("general-error-action")
                })
                logError(ELogErrorMsg.ErrorPublishingPopup, error)
            }).finally(() => {
                btn.reset()
            })
        }
    }

    async function handleBrandingToggle(): Promise<any> {
        if (popup) {
            return savePopup({
                dataToUpdate: {
                    branding: popup.branding === EPopupBranding.smart ? EPopupBranding.basic : EPopupBranding.smart
                },
                render: true
            }).then((data) => {
                if (data) {
                    return data?.content?.json || null
                }
            })
        }
    }


    function handleInlineFieldConfirm({ name, value }: { name?: string, value?: string }) {
        if (popup) {
            savePopup({ dataToUpdate: { [name as "name"]: value || "" } })
        }
    }


    function handleTag(tag: string, btn: TButtonRef) {
        if (popup) {
            savePopup({ dataToUpdate: { tags: [...(popup.tags || []), tag] } }).finally(() => {
                btn.reset()
            })
        }
    }

    function handleUntag(tag: string, btn: TButtonRef) {
        if (popup) {
            savePopup({ dataToUpdate: { tags: (popup.tags || []).filter((t) => t !== tag) } }).finally(() => {
                btn.reset()
            })
        }
    }

    function handleDDChanges(event: SelectChangeEvent<string>) {
        const { name, value } = event.target

        if (!popup) return

        const dataToUpdate = setDataInPath({ name, value, data: popup, merge: true })

        setPopup(new PopupModel({ ...popup, ...dataToUpdate }))
        savePopup({ dataToUpdate, render: ["content.position", "closeButton.position"].includes(name) })
    }


    async function handleOnEditorSave(jsonFile: string, htmlFile: string): Promise<any> {
        if (popup) {
            return savePopup({
                dataToUpdate: {
                    content: {
                        position: popup.content.position,
                        json: JSON.parse(jsonFile)
                    }
                },
                render: true
            })
        }
    }

    function handleOnClose() {
        window.location.href = "#" + fetchRoute({ route: ERoutes.popups })
    }

    function handlePopupEdit() {
        setIsEditing(true)
        window.location.href = "#" + fetchRoute({ route: ERoutes.popupEdit, params: { popupId: popupId } })
    }

    function handleOnEditorClose() {
        setIsEditing(false)
        window.location.href = "#" + fetchRoute({ route: ERoutes.popupDetails, params: { popupId: popupId } })
    }

    function validateFormUrls() {
        if (!form) return
        const defaultUrls = buildDefaultUrls({ list: selectedList, account: genericWrapper.account, user: genericWrapper.user, form: form })
        if (form?.post_redirect_url === "" || form?.double_opt_in_redirect_url === "") {
            const formToSave: Partial<EnhancedFormModel> = {}
            if (form?.post_redirect_url === "") {
                formToSave.post_redirect_url = defaultUrls.post_redirect_url
            }
            if (form?.double_opt_in_redirect_url === "") {
                formToSave.double_opt_in_redirect_url = defaultUrls.double_opt_in_redirect_url
            }
            saveForm(formToSave)
        }
    }

    async function savePopup({ dataToUpdate, render }: { dataToUpdate?: Partial<TPopupModel>, render?: boolean } = {}) {
        setLoading(true)
        const popupToSave = dataToUpdate || popup
        if (popupToSave) {
            validateFormUrls()
            return popup?.save(popupToSave).then((data) => {
                if (render) {
                    renderPopup()
                }
                setPopup(data)
                return data
            }).catch((error) => {
                DialogHandler.show({
                    type: "error",
                    title: t("general-error-action")
                })
                logError(ELogErrorMsg.ErrorSavingPopup, error)
            }).finally(() => {
                setLoading(false)
            })
        }
    }

    async function saveForm(dataToUpdate?: Partial<EnhancedFormModel | void>) {
        const formToSave = dataToUpdate || form
        if (formToSave) {
            return form?.save(formToSave).then((response) => {
                setForm(new EnhancedFormModel(response))
                return response
            }).catch((error) => {
                DialogHandler.show({
                    type: "error",
                    title: t("general-error-action"),
                })
                logError(ELogErrorMsg.ErrorSavingForm, error)
            })
        }
    }

    function handleTriggerValueChange({ name, value }: { name: string, value: any }) {
        if (popup) {
            popup.trigger[name as keyof TPopupTrigger] = value as never
            setPopup(new PopupModel(popup))
            savePopup({ dataToUpdate: { trigger: { ...popup.trigger } } })
        }
    }

    function handleTriggerConditionChange(condition: EPopupTriggerType) {
        if (popup) {
            popup.trigger.type = condition
            setPopup(new PopupModel(popup))
            savePopup({ dataToUpdate: { trigger: { ...popup.trigger, type: condition } } })
        }
    }

    function handleTextFieldChange(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
        const { name, value } = event.target
        if (popup) {
            let finalValue: any = value
            if (name === "overlay.opacity") {
                finalValue = parseFloat(value) / 100
                finalValue = finalValue > 1 ? 1 : finalValue
                finalValue = finalValue < 0 ? 0 : finalValue
            }

            const dataToUpdate = setDataInPath({ name, value: finalValue, data: popup, merge: true })
            setPopup(new PopupModel({ ...popup, ...dataToUpdate }))
            savePopup({ dataToUpdate, render: true })
        }
    }

    const isPublished = popup?.published_on && popup.published_on > 0
    const hasUnpublishedChanges = popup && ((popup?.published_on && popup.published_on > 0 && popup?.updated_on - 1 > popup?.published_on) || popup?.published_on == null)

    const layoutOptions = useMemo(() => [
        { text: t("general-center"), value: EPopupPosition.center },
        { text: t("general-top-right"), value: EPopupPosition.top_right },
        { text: t("general-top-left"), value: EPopupPosition.top_left },
        { text: t("general-bottom-left"), value: EPopupPosition.bottom_left },
        { text: t("general-bottom-right"), value: EPopupPosition.bottom_right },
        { text: t("general-drawer-left"), value: EPopupPosition.drawer_left },
        { text: t("general-drawer-right"), value: EPopupPosition.drawer_right },
        { text: t("general-drawer-top"), value: EPopupPosition.drawer_top },
        { text: t("general-drawer-bottom"), value: EPopupPosition.drawer_bottom },
    ], [])

    const triggerTypeDataMap = useMemo(() => {
        return {
            [EPopupTriggerType.on_page_load]: null,
            [EPopupTriggerType.on_time_delay]: "delay_seconds",
            [EPopupTriggerType.on_click]: "selector",
            [EPopupTriggerType.on_scroll]: "scroll_percentage",
            [EPopupTriggerType.on_exit_intent]: null
        }
    }, [])

    const displayFrequencyOptions = useMemo(() => {
        return [
            { text: t("general-always"), value: EPopupDisplayFrequency.always },
            { text: t("general-once"), value: EPopupDisplayFrequency.once },
            { text: t("general-once-per-day"), value: EPopupDisplayFrequency.once_per_day },
            { text: t("general-once-per-week"), value: EPopupDisplayFrequency.once_per_week },
            { text: t("general-once-per-session"), value: EPopupDisplayFrequency.once_per_session }
        ]
    }, [])

    return <Modal className={"editPopupModal"}
        open
        heading={{
            logoUrl: getBrandUrl({ brand: genericWrapper.partnerBrand, elKey: "logo" }),
            titleContent: [
                <Typography key="popupTitle" variant="title" color="body1.main">{t("general-popup_one")}</Typography>,
                ...(popup && [<Chip key="popupChip" label={t(isPublished ? "general-published" : "general-unpublished")} color={isPublished ? "secondary" : "primary"} />] || [])
            ],
            actions: [
                <Button
                    key="publish"
                    animated
                    disabled={!popup || loading}
                    color="secondary"
                    variant='contained' startIcon={<Icon name={hasUnpublishedChanges ? "Publish18" : "Checkmark14"} />}
                    onClick={handlePublish}
                >
                    {t(hasUnpublishedChanges ? "general-publish" : "general-published")}
                </Button>
            ],
            closeBtnText: t("general-close"),
            closeBtnOnClick: handleOnClose
        }}
        content={
            <LoadingContainer loading={!popup}>
                {popup && <ResourceDetailContainer>
                    <Stack gap={6}>
                        <FullBar
                            left={
                                <Typography variant="title" color="primary.main">
                                    <InlineTextEdit
                                        name="name"
                                        tooltipTitle={t("popups-details-name-tooltip")}
                                        onConfirm={handleInlineFieldConfirm}
                                        defaultValue={popup.name || ""}
                                        placeholder={t("general-untitled")}
                                    />
                                </Typography>

                            }
                            right={[
                                <Typography key="tagCount" variant="body1" className="semiBold">
                                    {popup.tags?.length || 0} {t("general-tag", { count: popup.tags?.length || 0 })}
                                </Typography>,
                                <AddTagsButton key="addTagsButton" tagList={popup.tags || []} onTag={handleTag} onUntag={handleUntag} />
                            ]}
                        />

                        <Stack gap={6}>
                            <Typography variant="title" color="shade500.main">
                                {t("popups-details-settings-title")}
                            </Typography>

                            <ResourceEdit
                                status="valid"
                                title={t("popups-details-layout-title")}
                                desc={t("popup-details-layout-desc")}
                                support={<Typography variant="body1">
                                    <Trans
                                        i18nKey={
                                            [
                                                EPopupPosition.drawer_left,
                                                EPopupPosition.drawer_right,
                                                EPopupPosition.drawer_top,
                                                EPopupPosition.drawer_bottom
                                            ].includes(popup.content.position) ? "popups-details-layout-support-drawer"
                                                : "popups-details-layout-support"
                                        }
                                        values={{
                                            layout: layoutOptions.find(
                                                (option) => option.value === popup.content.position
                                            )?.text.toLowerCase()
                                        }} />
                                </Typography>}
                                editable
                                editableContent={<Dropdown
                                    name="content.position"
                                    options={layoutOptions}
                                    value={popup.content.position}
                                    onChange={handleDDChanges}
                                />}
                            />

                            {/*<ResourceEdit
                            editable
                            status="valid"
                            title={t("general-targeting")}
                            desc={t("popup-details-targeting-desc")}
                            support={"Show popup to X targeting options"}
                            editableContent={"Editable content"}
                        />*/}
                            <ResourceEdit
                                editable
                                status="valid"
                                title={t("general-trigger_one")}
                                desc={t("popup-details-triggers-desc")}
                                support={<Stack gap={4} direction="row">
                                    <Typography variant="body1">
                                        <Trans
                                            i18nKey={"popup-details-triggers-support-" + popup.trigger.type}
                                            values={{ value: popup.trigger[triggerTypeDataMap[popup.trigger.type] as keyof TPopupTrigger] }}
                                        />
                                    </Typography>
                                    <Typography variant="body1">
                                        <Trans
                                            i18nKey={"popup-details-triggers-support-display"}
                                            values={{ value: t("general-" + popup.display_frequency.replace("_", "-")) }}
                                        />
                                    </Typography>
                                </Stack>}
                                editableContent={<Stack className="queryConditionContainer" gap={4}>
                                    <Typography variant="body1L" className="semiBold">
                                        {t("query-builder-trigger-group-title")}
                                    </Typography>

                                    <PopupSampleQueryCondition
                                        key={popup.trigger.type}
                                        onValueChange={handleTriggerValueChange}
                                        defaultValue={popup.trigger[triggerTypeDataMap[popup.trigger.type] as keyof TPopupTrigger]}
                                        condition={popup.trigger.type}
                                        onConditionChange={handleTriggerConditionChange}
                                    />

                                    <Typography variant="body1L" className="semiBold">
                                        {t("popups-details-display-frequency-title")}
                                    </Typography>
                                    <Dropdown
                                        name="display_frequency"
                                        options={displayFrequencyOptions}
                                        value={popup.display_frequency}
                                        onChange={handleDDChanges}
                                    />
                                </Stack>}
                            />


                            <ResourceEdit
                                editable
                                status="valid"
                                title={t("popups-details-overlay-tint-title")}
                                desc={t("popups-details-overlay-tint-desc")}
                                support={<Typography variant="body1">
                                    {t("general-color")} <b>{popup.overlay.color}</b> &nbsp; {t("general-opacity")} <b>{popup.overlay.opacity * 100}%</b>
                                </Typography>}
                                editableContent={<Stack gap={4} direction="row">
                                    <ColorTextField
                                        name="overlay.color"
                                        value={popup.overlay.color}
                                        onChange={handleTextFieldChange}
                                        label={t("general-color")}
                                    />
                                    <TextField
                                        name="overlay.opacity"
                                        defaultValue={popup.overlay.opacity * 100}
                                        label={t("general-opacity")}
                                        timeout={{
                                            callback: handleTextFieldChange
                                        }}
                                        type="number"
                                        inputProps={{
                                            min: 0,
                                            max: 100
                                        }}
                                    />
                                </Stack>}
                            />

                            <ResourceEdit
                                editable
                                status="valid"
                                title={t("popups-details-close-button-title")}
                                desc={t("popups-details-close-button-desc")}
                                support={<Typography variant="body1">
                                    {t("general-position")} <b>{t("general-" + popup.closeButton.position)}</b> &nbsp; {t("general-color")} <b>{popup.closeButton.color}</b>
                                </Typography>}
                                editableContent={<Stack gap={4} direction="row">
                                    <Dropdown
                                        size="large"
                                        name="closeButton.position"
                                        label={t("general-position")}
                                        options={[
                                            { text: t("general-top-right"), value: EPopupCloseButtonPosition.top_right },
                                            { text: t("general-top-left"), value: EPopupCloseButtonPosition.top_left },
                                            { text: t("general-bottom-left"), value: EPopupCloseButtonPosition.bottom_left },
                                            { text: t("general-bottom-right"), value: EPopupCloseButtonPosition.bottom_right }
                                        ]}
                                        value={popup.closeButton.position}
                                        onChange={handleDDChanges}
                                    />
                                    <ColorTextField
                                        name="closeButton.color"
                                        value={popup.closeButton.color}
                                        onChange={handleTextFieldChange}
                                        label={t("general-color")}
                                    />
                                </Stack>}
                            />

                            <ResourceEdit
                                title={t("general-embed-code")}
                                desc={t("popup-embed-snippet-desc")}
                                support={popup?.published_on && popup.published_on > 0 ?
                                    <ResourceCopy
                                        isCodeBlock
                                        text={popup?.content.published_content || ""} />
                                    :
                                    <Typography>{t("popup-embed-snippet-not-published")}</Typography>
                                }
                            />
                        </Stack>

                        {form && <Stack gap={6}>
                            <Typography variant="title" color="shade500.main">
                                {t("forms-details-settings-title")}
                            </Typography>

                            <ResourceEdit
                                title={t("general-audience_one")}
                                desc={t("form-audience-desc")}
                                support={<Typography variant="body1">{t("form-audience-support")} <b>'{selectedList?.name || ""}'</b></Typography>}
                            />

                            <FromCommonResourceEdit
                                form={form}
                                content={popup?.content}
                                saveForm={saveForm}
                                saveContent={savePopup}
                            />

                        </Stack>}

                    </Stack>



                    <ResourcePreview
                        loading={renderLoading || loading}
                        content={previewContent}
                        actions={[
                            <Button
                                key="edit"
                                color="primary"
                                variant='contained'
                                size="small"
                                onClick={handlePopupEdit} >
                                {t("general-edit-popup")}
                            </Button>
                        ]}
                    />

                    <BeeEditor
                        key={popup?.content.position}
                        hidden={!isEditing}
                        titleContent={<Typography variant="title" >{t("general-edit-popup")}</Typography>}
                        onClose={handleOnEditorClose}
                        editorType={EEditorType.popUpDesigner}
                        initContent={popup?.content?.json}
                        onSave={handleOnEditorSave}
                        onBrandingToggle={handleBrandingToggle}
                        contentContext={{
                            popupPosition: popup?.content.position,
                            list: selectedList,
                            formAction: form?.submission_url,
                            isSmart: popup?.branding === "smart",
                            recaptchaSiteKey: form?.recaptcha?.site_key
                        }}
                    />


                </ResourceDetailContainer>
                }
            </ LoadingContainer>
        }
    />
}